import React, { useEffect, useState } from 'react';
import './ChevronProcessDiagram.scss';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { ChevronDiagramStage } from './models/ChevronDiagramStage';
import { STAGE_ITEMS } from '../Constants/dropdownItems';
import { ChevronDiagramColumn } from './ChevronDiagramColumn';
import { ChevronDiagramEvent } from './models/ChevronDiagramEvent';
import {
    getEventDeadlineDateUtc,
    getEventStartDateUtc,
} from '../../../slices/marketplace/timeline/utils/timeline-dates';
import { DateTimeHelper } from '../../../utils/dateTimeHelper';
import Helpers from '../../../utils/helper';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { TimelineClosingDate } from '../../../slices/marketplace/closing-date/models/timeline-closing-date.model';
interface ChevronProcessDiagramProps {
    timelineEvents: TimelineEvent[];
    dataRequestProjects?: LinkedDataRequestProject[];
    engagementStartDate: Date;
    onActionClick(item: ChevronDiagramEvent): void;
    isEventActionVisible: (eventId: string) => boolean;
    isTempoPreview?: boolean;
    engagementId: number;
    isBuyerView: boolean;
    timelineId?: number;
    hideExecuteButton:boolean;
    buyerActionState?: EngagementBuyerActionState[];
    closingDate?: TimelineClosingDate | null;
}

export const ChevronProcessDiagram = (props: ChevronProcessDiagramProps) => {
    const [chevronDiagramStages, setChevronDiagramStages] =
        useState<ChevronDiagramStage[]>();
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const party = useCurrentMarketplaceParty(props.engagementId);

    useEffect(() => {
        setChevronDiagramStages(groupTimelineEvents(props.timelineEvents));
        setIsLoading(false);
    }, [props.timelineEvents]);

    // A buyer sees a buyer or shared step as done once their own part is done, even while the step stays open
    // for the other buyers (e.g. the interest window) - so the date shown is the day they finished their part.
    const buyersOwnCompletedPart = (
        event: TimelineEvent
    ): EngagementBuyerActionState | undefined => {
        const isBuyerPartyEvent =
            event.party === TimelineParty.Buyer ||
            event.party === TimelineParty.SellerAndBuyer;

        if (
            party !== TimelineParty.Buyer ||
            !isBuyerPartyEvent ||
            event.linkedAction == null
        ) {
            return undefined;
        }

        return props.buyerActionState?.find(
            (as) =>
                as.actionId === event.linkedAction.id &&
                as.status === TimelineActionStatus.Completed
        );
    };

    const convertToChevronDiagramEvent = (
        event: TimelineEvent
    ): ChevronDiagramEvent => {
        const project =
            party !== TimelineParty.Buyer &&
            props.dataRequestProjects
                ? props.dataRequestProjects?.find(
                      (p) => p.id == event.linkedAction?.referenceItemId
                  )
                : undefined;
        const ownCompletedPart = buyersOwnCompletedPart(event);
        const ownCompletedAt =
            ownCompletedPart?.dateModified ?? ownCompletedPart?.dateCreated;

        return {
            id: event.id,
            masterEventId: event.masterEventId,
            name: event.name,
            party: event.party,
            dayStart: getEventStartDateUtc(props.engagementStartDate, event),
            deadline: getEventDeadlineDateUtc(props.engagementStartDate, event),
            dayCompleted: ownCompletedAt
                ? DateTimeHelper.parseUtc(ownCompletedAt)
                : event.completedAt
                  ? DateTimeHelper.parseUtc(event.completedAt)
                  : null,
            status: ownCompletedPart
                ? TimelineEventStatus.Completed
                : event.status,
            linkedAction: event.linkedAction,
            actionLinkText: project?.actionLinkText,
            isSuspendEvent: event.isSuspendEvent,
        };
    };

    const groupTimelineEvents = (
        events: TimelineEvent[]
    ): ChevronDiagramStage[] => {
        const stageMap = new Map<number, ChevronDiagramStage>();

        events.forEach((event) => {
            const { stage, stageName } = event;

            if (!stageMap.has(stage)) {
                const index = stageMap.size;
                const color = STAGE_ITEMS[index % STAGE_ITEMS.length].color;

                stageMap.set(stage, {
                    name: stageName,
                    backgroundColor: color,
                    events: [],
                });
            }

            stageMap
                .get(stage)!
                .events.push(convertToChevronDiagramEvent(event));
        });

        return Array.from(stageMap.values());
    };

    return (
        <div className="chevron-process-diagram">
            <div id="phases">
                {isLoading ? (
                    Helpers.renderPageLoader()
                ) : (
                    <ul>
                        {chevronDiagramStages.map((stage, index) => (
                            <li>
                                <ChevronDiagramColumn
                                    chevronDiagramStage={stage}
                                    onActionClick={props.onActionClick}
                                    isEventActionVisible={
                                        props.isEventActionVisible
                                    }
                                    party={party}
                                    isTempoPreview={props.isTempoPreview}
                                    engagementId={props.engagementId}
                                    isBuyerView={props.isBuyerView}
                                    timelineId={props.timelineId}
                                    hideExecuteButton={props.hideExecuteButton}
                                    closingDate={
                                        index === chevronDiagramStages.length - 1
                                            ? props.closingDate
                                            : null
                                    }
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
